import { gql } from '@apollo/client';

export const MyBoardsHome = gql`
  query MyBoardsHome($withLabels: Boolean!) {
    id
    allBucketCounts {
      id
      critical
      high
      medium
      low
      minimal
    }
    gigCountsByRule {
      id
      critical {
        rule
        count
      }
      high {
        rule
        count
      }
      medium {
        rule
        count
      }
      low {
        rule
        count
      }
      minimal {
        rule
        count
      }
    }
    myBoards {
      id
      name
      priority
      boardColor
      counts {
        id
        title
        priority
        gigCount
      }
      labels @include(if: $withLabels) {
        id
        ...LabelDetailsFragment
      }
    }
    labels @include(if: $withLabels) {
      id
      boardCount
      ...LabelDetailsFragment
    }
  }
`;

export const MyBoardsWithLabel = gql`
  query MyBoardsHome {
    id
    myBoards {
      id
      name
      priority
      boardColor
      counts {
        id
        title
        priority
        gigCount
      }
    }
  }
`;

export const MyBoardsWithBucketsQuery = gql`
  query MyBoardsWithBucketsQuery($boardId: ID!, $filter: FilterInput, $priorities: [String!]) {
    board(id: $boardId) {
      id
      ...BoardFragment
      lastCacheUpdatedAt
      buckets(priorities: $priorities) {
        id
        ...BucketWithFilteredGigsCountFragment
      }
    }
  }
`;

export const MyBoardsWithGroupedGigBucketsQuery = gql`
  query MyBoardsWithGroupedGigBucketsQuery(
    $boardId: ID!
    $filter: FilterInput
    $priorities: [String!]
  ) {
    board(id: $boardId) {
      id
      ...BoardFragment
      lastCacheUpdatedAt
      buckets(priorities: $priorities) {
        id
        ...BucketWithFilteredGigGroupCountFragment
      }
    }
  }
`;

export const MyBoardsWithGroupedGigBucketsByPriorityAndRule = gql`
  query MyBoardsWithGroupedGigBucketsByPriorityAndRule(
    $priority: String!
    $title: String
    $filter: FilterInput
  ) {
    myBoards {
      id
      ...BoardFragment
      bucketsByPriority(priority: $priority, title: $title) {
        id
        ...BucketWithFilteredGigGroupCountFragment
      }
      labels {
        id
        ...LabelDetailsFragment
      }
    }
    labels {
      id
      ...LabelDetailsFragment
    }
  }
`;

export const MyBoardsWithBucketsByPriorityAndRule = gql`
  query MyBoardsWithPriorityBuckets(
    $priority: String!
    $title: String
    $filter: FilterInput
    $withLabels: Boolean!
  ) {
    myBoards {
      id
      ...BoardFragment
      bucketsByPriority(priority: $priority, title: $title) {
        id
        ...BucketWithFilteredGigsCountFragment
      }
      labels @include(if: $withLabels) {
        id
        ...LabelDetailsFragment
      }
    }
    labels @include(if: $withLabels) {
      id
      ...LabelDetailsFragment
    }
  }
`;

export const GigsByBucket = gql`
  query GigsByBucket(
    $order: String
    $boardId: ID!
    $pageNumber: Int!
    $recordsPerPage: Int!
    $bucketId: String!
    $filter: FilterInput
  ) {
    myBoards(id: $boardId) {
      id
      showFirstAndLastName
      textlineGroupUuid
      buckets(bucketId: $bucketId) {
        id
        ...BucketFragment
        gigs(
          order: $order
          pageNumber: $pageNumber
          recordsPerPage: $recordsPerPage
          filter: $filter
        ) {
          id
          ...GigBasicDetailsFragment
          deliveryLocation {
            id
            timezone
          }
          driver {
            id
            ...DriverBasicDetailsFragment
          }
          consolidation {
            id
            ...GigConsolidationDetailsFragment
          }
        }
      }
    }
  }
`;

export const GroupedGigsByBucket = gql`
  query GroupedGigsByBucket(
    $order: String
    $boardId: ID!
    $pageNumber: Int!
    $recordsPerPage: Int!
    $bucketId: String!
    $filter: FilterInput
  ) {
    myBoards(id: $boardId) {
      id
      showFirstAndLastName
      textlineGroupUuid
      buckets(bucketId: $bucketId) {
        id
        ...GroupedGigBucketFragment
        groupedGigs(
          order: $order
          pageNumber: $pageNumber
          recordsPerPage: $recordsPerPage
          filter: $filter
        ) {
          isDriverAssigned
          gigCount
          driver {
            id
            verifiedAvatarUrl
            ...DriverBasicDetailsFragment
          }
          lastEvent {
            ...GigEventFragment
          }
          gigs {
            id
            ...GigBasicDetailsFragment
            deliveryLocation {
              id
              timezone
            }
            driver {
              id
              ...DriverBasicDetailsFragment
            }
          }
        }
      }
    }
  }
`;

export const GroupedGigsByPriorityBucket = gql`
  query GroupedGigsByPriorityBucket(
    $priority: String!
    $order: String
    $title: String
    $boardId: ID!
    $bucketId: ID!
    $pageNumber: Int!
    $recordsPerPage: Int!
    $filter: FilterInput
  ) {
    myBoards(id: $boardId) {
      id
      showFirstAndLastName
      textlineGroupUuid
      bucketsByPriority(
        priority: $priority
        title: $title
        bucketId: $bucketId
      ) {
        id
        ...GroupedGigBucketFragment
        groupedGigs(
          order: $order
          pageNumber: $pageNumber
          recordsPerPage: $recordsPerPage
          filter: $filter
        ) {
          isDriverAssigned
          gigCount
          driver {
            id
            verifiedAvatarUrl
            ...DriverBasicDetailsFragment
          }
          lastEvent {
            ...GigEventFragment
          }
          gigs {
            id
            ...GigBasicDetailsFragment
            deliveryLocation {
              id
              timezone
            }
            driver {
              id
              ...DriverBasicDetailsFragment
            }
          }
        }
      }
    }
  }
`;

export const GigsByPriorityBucket = gql`
  query GigsByPriorityBucket(
    $priority: String!
    $order: String
    $title: String
    $boardId: ID!
    $bucketId: ID!
    $pageNumber: Int!
    $recordsPerPage: Int!
    $filter: FilterInput
  ) {
    myBoards(id: $boardId) {
      id
      showFirstAndLastName
      textlineGroupUuid
      bucketsByPriority(
        priority: $priority
        title: $title
        bucketId: $bucketId
      ) {
        id
        ...BucketFragment
        gigs(
          order: $order
          pageNumber: $pageNumber
          recordsPerPage: $recordsPerPage
          filter: $filter
        ) {
          id
          ...GigBasicDetailsFragment
          deliveryLocation {
            id
            timezone
          }
          driver {
            id
            ...DriverBasicDetailsFragment
          }
          consolidation {
            id
            ...GigConsolidationDetailsFragment
          }
        }
      }
    }
  }
`;
